import { useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { RegulationContext } from "../../../../../App";
import ConfirmationModal from "../../../../../components/ConfirmationModal/ConfirmationModal";
import useFinalizeStatusPoller from "../../../../../core/hooks/useFinalizeStatusPoller";
import useHasPermission from "../../../../../core/hooks/useHasPermission";
import GHGEmissionService from "../../../../../core/service/GHGEmissionService";
import StandardService from "../../../../../core/service/StandardService";
import { toast } from "react-toastify";
import EmissionEditModal from "./EmissionEditModal";
import "./EmissionManagementPanel.scss";

const EmissionManagementPanel = ({ t, show, onHide, metric, forSuperAdmin = false }) => {
  const { isFinalizeInProgress } = useFinalizeStatusPoller();
  const { hasConfigUpdatePermission } = useHasPermission();
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const [metricDetails, setMetricDetails] = useState(null);
  const [dataPoints, setDataPoints] = useState([]);
  const [editingMetric, setEditingMetric] = useState(false);
  const [editingDataPoint, setEditingDataPoint] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEmissionStatus, setFilterEmissionStatus] = useState("all");
  const [filterLinkStatus, setFilterLinkStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    if (!show || !metric?.id) return;
    loadData();
    setSearchTerm("");
    setFilterEmissionStatus("all");
    setFilterLinkStatus("all");
    setSortBy("name");
    setExpandedRowId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, metric?.id]);

  const loadData = () => {
    if (!metric?.id) return;
    Promise.all([
      StandardService.getMetricDetails(metric.id, forSuperAdmin),
      StandardService.getDataPoints(metric.id, forSuperAdmin),
    ])
      .then(([metricRes, dpRes]) => {
        setMetricDetails(metricRes.data);
        const dps = dpRes?.data?.body?.dataPoints;
        setDataPoints(Array.isArray(dps) ? dps : []);
      })
      .catch(() => { });
  };

  const canEdit = () => hasConfigUpdatePermission() && !isFinalizeInProgress;

  const getCounts = () => {
    const total = dataPoints.length;
    const enabled = dataPoints.filter((dp) => dp.emission).length;
    const linked = dataPoints.filter((dp) => dp.emissionLinked).length;
    return {
      total,
      enabled,
      disabled: total - enabled,
      linked,
      pending: Math.max(enabled - linked, 0),
    };
  };

  const getFilteredSortedDataPoints = () => {
    let filtered = dataPoints.filter((dp) =>
      (dp.name || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterEmissionStatus !== "all") {
      filtered = filtered.filter((dp) => (filterEmissionStatus === "enabled" ? dp.emission : !dp.emission));
    }
    if (filterLinkStatus !== "all") {
      filtered = filtered.filter((dp) => (filterLinkStatus === "linked" ? dp.emissionLinked : !dp.emissionLinked));
    }

    filtered = [...filtered].sort((a, b) => {
      if (sortBy === "status") {
        return (b.emission ? 1 : 0) - (a.emission ? 1 : 0);
      }
      return (a.name || "").localeCompare(b.name || "");
    });

    return filtered;
  };

  const toggleExpand = (dp) => {
    setExpandedRowId(expandedRowId === dp.id ? null : dp.id);
  };

  const handleEditSaved = () => {
    setEditingMetric(false);
    setEditingDataPoint(null);
    loadData();
  };

  const requestUnlink = (type, entity) => {
    setUnlinkTarget({ type, entity });
  };

  // Reaffirms the datapoint's last-saved emission enable/table/version/scope/category
  // unchanged - Unlink must only ever clear the Emission Source/Factor link, nothing
  // else on the datapoint itself.
  const buildDataPointUnlinkPreservePayload = (dp) => ({
    unitOfMeasure: dp.unitOfMeasure,
    code: dp.code,
    metricId: metric?.id,
    relevantUoms: dp.relevantUoms ?? [],
    emission: dp.emission,
    scope: dp.emission ? (dp.scope || "") : "",
    scopeCategory: dp.emission ? (dp.scopeCategory || "") : "",
    emissionDatabase: dp.emission ? (dp.emissionDatabase || dp.database || "") : "",
    emissionVersionRef: dp.emission ? (dp.emissionVersionRef || dp.versionRef || "") : "",
    frequency: dp.frequency ?? null,
    groups: dp.groups ?? null,
    altText: dp.altText || null,
    helpText: dp.helpText || null,
    fileUpload: dp.fileUpload,
    comments: dp.comments,
    applicable: dp.applicable,
    name: dp.name,
  });

  const handleUnlinkConfirmed = (confirmed) => {
    const target = unlinkTarget;
    setUnlinkTarget(null);
    if (!confirmed || !target) return;

    const isDataPoint = target.type === "dataPoint";
    const deleteReq = isDataPoint
      ? GHGEmissionService.deleteDatapointEmissionLinking(glbRegulationId, target.entity.id)
      : GHGEmissionService.deleteMetricEmissionLinking(glbRegulationId, target.entity.id);

    deleteReq
      .then(() => {
        if (!isDataPoint) return Promise.resolve();
        return StandardService.updateDataPoint(
          buildDataPointUnlinkPreservePayload(target.entity),
          target.entity.id,
          forSuperAdmin
        );
      })
      .then(() => {
        toast.success("Success", { position: toast.POSITION.TOP_RIGHT });
        loadData();
      })
      .catch(() => { });
  };

  const renderEmissionBadge = (enabled) => (
    <span className={`badge ${enabled ? "bg-badge-Active" : "bg-badge-Corrected "} rounded-pill me-2`}>
      {t("Emission.label")} {t(enabled ? "Enabled.label" : "Disabled.label")}
    </span>
  );

  const renderLinkBadge = (dp) => {
    if (!dp.emission) return <span className="text-muted">—</span>;
    if (dp.emissionLinked) {
      return <span className="badge bg-success">{t("EmissionFactorlinked.label")}</span>;
    }
    return (
      <span className="badge bg-warning text-dark">
        {/* {t("EmissionManagement.missingEmissionFactor.label")} */}
      </span>
    );
  };

  const renderMetricHeader = () => {
    if (!metricDetails) return null;
    return (
      <div className="mb-3">
        <div className="emission-metric-header-title mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0"> {metric?.name}</h5>
          </div>
          <div className="d-flex align-items-center gap-2">
            {canEdit() && metricDetails.emissionLinked && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => requestUnlink("metric", metricDetails)}
              >
                {t("Unlink.label")}
              </button>
            )}
            {canEdit() && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setEditingMetric(true)}
              >
                {t("Edit.label")}
              </button>
            )}
          </div>
        </div>
        {metricDetails.emission && (
          <div className="emission-metric-header p-3 mb-3">

            <div className="row mt-2">
              <div className="col-md-2">
                <small className="text-muted d-block">{t("EmissionTable.label")}</small>
                {metricDetails.emissionDatabase || metricDetails.database || "-"}
              </div>
              <div className="col-md-2">
                <small className="text-muted d-block">{t("Version.label")}</small>
                {metricDetails.emissionVersionRef || metricDetails.versionRef || "-"}
              </div>
              <div className="col-md-2">
                <small className="text-muted d-block">{t("Scope.label")}</small>
                {metricDetails.scope || "-"}
              </div>
              <div className="col-md-3">
                <small className="text-muted d-block">{t("Category.label")}</small>
                {metricDetails.scopeCategory || "-"}
              </div>
              <div className="col-md-2">
                <small className="text-muted d-block">{t("Status.label")}</small>
                {renderEmissionBadge(metricDetails.emission)}
                </div>
            </div>
          </div>

        )}
      </div>
    );
  };



  const renderDataPointRow = (dp) => {
    const isExpanded = expandedRowId === dp.id;
    return (
      <>
        <tr key={dp.id} className="emission-dp-row" onClick={() => toggleExpand(dp)}>
          <td>{dp.name}</td>
          <td>{renderEmissionBadge(dp.emission)}</td>
          <td>{renderLinkBadge(dp)}</td>
          <td>{dp.emission ? (dp.emissionDatabase || dp.database || "-") : "-"}</td>
          <td>{dp.emission ? (dp.scope || "-") : "-"}</td>
          <td>{dp.emission ? (dp.scopeCategory || "-") : "-"}</td>
          <td onClick={(e) => e.stopPropagation()} className="d-flex align-items-center gap-2">
            {canEdit() && dp.emissionLinked && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => requestUnlink("dataPoint", dp)}
              >
                {t("Unlink.label")}
              </button>
            )}
            {canEdit() && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setEditingDataPoint(dp)}
              >
                {t(dp.emission ? "Edit.label" : "EmissionManagement.enable.label")}
              </button>
            )}
          </td>
        </tr>
        {isExpanded && (
          <tr className="emission-row-expand">
            <td colSpan={7}>
              <div className="row">
                <div className="col-md-3">
                  <small className="text-muted d-block">{t("EmissionTable.label")}</small>
                  {dp.emissionDatabase || dp.database || "-"}
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">{t("Version.label")}</small>
                  {dp.emissionVersionRef || dp.versionRef || "-"}
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">{t("Scope.label")}</small>
                  {dp.scope || "-"}
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">{t("Category.label")}</small>
                  {dp.scopeCategory || "-"}
                </div>
              </div>
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderDataPointsTable = () => {
    const rows = getFilteredSortedDataPoints();
    return (
      <div className="emission-datapoints-table">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>{t("DataPoint.label")}</th>
              <th>{t("EmissionManagement.emissionStatus.label")}</th>
              <th>{t("EmissionManagement.linkStatus.label")}</th>
              <th>{t("EmissionTable.label")}</th>
              <th>{t("Scope.label")}</th>
              <th>{t("Category.label")}</th>
              <th>{t("Action.label")}</th>
            </tr>
          </thead>
          <tbody>{rows.map((dp) => renderDataPointRow(dp))}</tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-90w" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          {t("EmissionManagement.title.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderMetricHeader()}
        {renderDataPointsTable()}
      </Modal.Body>

      {editingMetric && metricDetails && (
        <EmissionEditModal
          show={editingMetric}
          onHide={() => setEditingMetric(false)}
          entityType="metric"
          entity={metricDetails}
          forSuperAdmin={forSuperAdmin}
          onSaveSuccess={handleEditSaved}
        />
      )}

      {editingDataPoint && (
        <EmissionEditModal
          show={Boolean(editingDataPoint)}
          onHide={() => setEditingDataPoint(null)}
          entityType="dataPoint"
          entity={editingDataPoint}
          metricId={metric?.id}
          forSuperAdmin={forSuperAdmin}
          onSaveSuccess={handleEditSaved}
        />
      )}

      {unlinkTarget && (
        <ConfirmationModal
          modalShow={Boolean(unlinkTarget)}
          messageText={t("UnlinkEmissionFactorConfirmation.label")}
          callbackModal={handleUnlinkConfirmed}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </Modal>
  );
};

export default withTranslation()(EmissionManagementPanel);
